import { c as _c } from "react/compiler-runtime";
// Промокод в корзине веба (PDF «Доработки платформы» п.1 и п.3, Figma «5.
// Корзина» 4140:45301 / 4141:45676 / 4140:45362 / 4141:45627 — кадры
// приложения, здесь — та же механика в сводке веб-корзины).
//
//   • поле h40 r10: код вводится, применяется кнопкой-стрелкой или Enter
//     («Активация через кнопку» — без применения на blur);
//   • применённый код — в поле, справа крестик «убрать»;
//   • отказ — красная обводка и текст СЕРВЕРА под полем («Не хватает 872
//     руб., добавьте товары, которые участвуют в акции», «Этот код действует
//     только для первых заказов в магазине» …), без тостов;
//   • под полем — лента карточек-билетов (GET /cart/:id/promos): коды
//     покупателя и общедоступные коды магазинов из корзины, «Применить» на
//     корешке;
//   • код из ссылки подставляется сам: `?apply=КОД` (из «Моих промокодов») и
//     партнёрская `?promo=КОД` (App.tsx запоминает её на 30 дней) — один раз
//     на корзину, снятый покупателем код обратно не возвращается.

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { api } from "@/api";
import { XIcon } from "@/components/icons";
import { Input } from "@/components/ui/text-field";
import { markReferralApplied, referralApplyPendingFor } from "@/lib/referral-promo";
import { useCart } from "@/store";
import { PromoTicket } from "./promo-ticket";
const normalize = (v: string) => v.toUpperCase().replace(/\s/g, "").slice(0, 64);

/** «Стрелка вправо в круге» из макета (Outline/Arrow - Right Circle, 20). */
function ArrowRightCircle20() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden={true}><circle cx="10" cy="10" r="8.25" stroke="currentColor" strokeWidth="1.5" /><path d="M6.5 10h6.5M10.5 7.25 13.25 10l-2.75 2.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function CartPromo({
  wholeCart
}: {
  wholeCart: boolean;
}) {
  const cart = useCart(s => s.cart);
  const applyPromo = useCart(s => s.applyPromo);
  const clearPromo = useCart(s => s.clearPromo);
  const qc = useQueryClient();
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const cartId = cart?.id ?? null;
  const applied = cart?.promoCode ?? null;
  const hasItems = (cart?.items ?? []).some(i => i.available);
  const feed = useQuery({
    queryKey: ["cart", cartId, "promos", applied ?? "", cart?.updatedAt ?? ""],
    queryFn: () => api.cart.promoFeed(cartId as string),
    enabled: Boolean(cartId) && hasItems,
    staleTime: 30_000
  });
  const cards = feed.data?.items ?? [];
  const apply = async (raw: string) => {
    const code = normalize(raw);
    if (!code || busy) return;
    setDraft(code);
    setBusy(code);
    setError(null);
    try {
      await applyPromo(code);
      setDraft("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Промокод не применён");
    } finally {
      setBusy(null);
      void qc.invalidateQueries({
        queryKey: ["cart", cartId, "promos"]
      });
    }
  };
  const drop = async () => {
    if (busy) return;
    setBusy("clear");
    try {
      await clearPromo();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не удалось убрать промокод");
    } finally {
      setBusy(null);
    }
  };

  // Код из ссылки: ?apply= (свой кабинет) или запомненная партнёрская ?promo=.
  const [params, setParams] = useSearchParams();
  const linkHandled = useRef(false);
  useEffect(() => {
    if (linkHandled.current || !cartId || !hasItems) return;
    const fromApply = params.get("apply")?.trim();
    if (fromApply) {
      linkHandled.current = true;
      const next = new URLSearchParams(params);
      next.delete("apply");
      setParams(next, {
        replace: true
      });
      if (applied?.toUpperCase() !== fromApply.toUpperCase()) void apply(fromApply);
      return;
    }
    if (applied) return;
    const referral = referralApplyPendingFor(cartId);
    if (!referral) return;
    linkHandled.current = true;
    markReferralApplied(cartId);
    void apply(referral);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartId, hasItems, applied, params]);
  const value = applied ?? draft;
  return <div className="flex min-w-0 flex-col gap-2.5" data-testid="cart-promo">
			<div className="flex flex-col gap-1">
				<Input variant="square" value={value} readOnly={Boolean(applied)} invalid={Boolean(error) && !applied} onChange={e => {
        setDraft(normalize(e.target.value));
        setError(null);
      }} onKeyDown={e => {
        if (e.key === "Enter") void apply(draft);
      }} placeholder="Промокод" aria-label="Промокод" aria-describedby={error ? "cart-promo-error" : undefined} className="font-normal uppercase placeholder:normal-case" fieldClassName="bg-white border-[#1F1F1F]/12" data-testid="cart-promo-input" end={applied ? <button type="button" onClick={() => void drop()} disabled={Boolean(busy)} aria-label="Убрать промокод" data-testid="cart-promo-clear" className="grid size-6 place-items-center rounded-full text-[#1F1F1F] outline-none hover:bg-[#1F1F1F]/6 focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40">
								<XIcon size={16} />
							</button> : draft ? <button type="button" onClick={() => void apply(draft)} disabled={Boolean(busy)} aria-label="Применить промокод" data-testid="cart-promo-submit" className="grid size-6 place-items-center rounded-full text-black outline-none hover:text-osmix-tangelo focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40 disabled:opacity-50">
								<ArrowRightCircle20 />
							</button> : null} />
				{error && !applied && <p id="cart-promo-error" role="alert" className="text-[12px] leading-4 text-ds-error" data-testid="cart-promo-error">
						{error}
					</p>}
				{applied && !wholeCart && <p className="text-[12px] leading-4 text-[#1F1F1F]/48">Промокод действует при оформлении всей корзины</p>}
			</div>

			{cards.length > 0 && <ul className="-mx-2.5 flex scroll-px-2.5 snap-x snap-mandatory items-start gap-2.5 overflow-x-auto px-2.5 pb-1 [scrollbar-width:thin]" aria-label="Ваши промокоды" data-testid="cart-promo-feed">
					{cards.map((c, i) => <li key={c.code} className="snap-start">
							<PromoTicket coupon={c} index={i} action={c.applied ? {
          label: "Применён",
          disabled: true
        } : {
          label: "Применить",
          onClick: () => void apply(c.code),
          pending: busy === c.code,
          disabled: Boolean(busy) || !hasItems
        }} note={c.applied ? null : c.message} />
						</li>)}
				</ul>}
		</div>;
}